import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import './DownloadAdvancedModal.css';

/** Duraciones disponibles para la descarga (minutos). */
export const DOWNLOAD_DURATION_OPTIONS = [5, 10] as const;
export type DownloadDurationMinutes = (typeof DOWNLOAD_DURATION_OPTIONS)[number];
const DEFAULT_DURATION_MINUTES: DownloadDurationMinutes = 5;

export interface DownloadAdvancedOptions {
  startTime: Date;
  endTime: Date;
  /** Duración elegida en minutos (5 | 10). */
  durationMinutes: number;
}

export interface DownloadAdvancedModalProps {
  open: boolean;
  onClose: () => void;
  cameraName: string;
  defaultStart: Date;
  /** Duración preseleccionada (minutos). Por defecto 5. */
  defaultDurationMinutes?: number;
  minDate?: Date;
  maxDate?: Date;
  onConfirm: (options: DownloadAdvancedOptions) => void;
}

function formatDateTime(d: Date): string {
  if (!Number.isFinite(d.getTime())) return '--';
  const pad = (n: number) => n.toString().padStart(2, '0');
  const h24 = d.getHours();
  const isPm = h24 >= 12;
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${h12}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${isPm ? 'p. m.' : 'a. m.'}`;
}

function toInputDateTime(d: Date): string {
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function fromInputDateTime(s: string): Date {
  return new Date(s);
}

function normalizeDuration(value: number | undefined): DownloadDurationMinutes {
  return (DOWNLOAD_DURATION_OPTIONS as readonly number[]).includes(value ?? NaN)
    ? (value as DownloadDurationMinutes)
    : DEFAULT_DURATION_MINUTES;
}

export const DownloadAdvancedModal: React.FC<DownloadAdvancedModalProps> = ({
  open,
  onClose,
  cameraName,
  defaultStart,
  defaultDurationMinutes,
  minDate,
  maxDate,
  onConfirm,
}) => {
  const [startTime, setStartTime] = useState(() => toInputDateTime(defaultStart));
  const [durationMinutes, setDurationMinutes] = useState<DownloadDurationMinutes>(() =>
    normalizeDuration(defaultDurationMinutes)
  );
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    submittingRef.current = false;
    setStartTime(toInputDateTime(defaultStart));
    setDurationMinutes(normalizeDuration(defaultDurationMinutes));
  }, [open, defaultStart, defaultDurationMinutes]);

  const startDate = fromInputDateTime(startTime);
  const startValid = Number.isFinite(startDate.getTime());
  // El fin se deriva de la duración elegida; se acota a maxDate (normalmente "ahora")
  // para no pedir tramos que aún no existen en disco.
  const rawEndDate = startValid
    ? new Date(startDate.getTime() + durationMinutes * 60 * 1000)
    : startDate;
  const endDate = startValid && maxDate && rawEndDate.getTime() > maxDate.getTime() ? maxDate : rawEndDate;
  const validRange =
    startValid &&
    (!minDate || startDate.getTime() >= minDate.getTime()) &&
    (!maxDate || startDate.getTime() < maxDate.getTime()) &&
    endDate.getTime() > startDate.getTime();

  const handleConfirm = useCallback(() => {
    if (!validRange || submittingRef.current) return;
    submittingRef.current = true;
    onConfirm({
      startTime: startDate,
      endTime: endDate,
      durationMinutes,
    });
    onClose();
  }, [validRange, startDate, endDate, durationMinutes, onConfirm, onClose]);

  if (!open) return null;

  return (
    <div className="download-advanced-overlay">
      <div
        className="download-advanced-modal"
        role="dialog"
        aria-labelledby="download-advanced-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="download-advanced-header">
          <h2 id="download-advanced-title">Descargar grabación</h2>
          <button type="button" className="download-advanced-close" onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>
        <div className="download-advanced-body">
          <p className="download-advanced-camera">{cameraName}</p>

          <div className="download-advanced-field">
            <label htmlFor="download-advanced-start">Fecha y hora de inicio</label>
            <div className="download-advanced-datetime">
              <input
                id="download-advanced-start"
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                step={1}
                min={minDate ? toInputDateTime(minDate) : undefined}
                max={maxDate ? toInputDateTime(maxDate) : undefined}
                aria-label="Fecha y hora de inicio de la descarga"
              />
            </div>
          </div>

          <div className="download-advanced-field">
            <label>Duración</label>
            <div
              className="download-advanced-duration"
              role="radiogroup"
              aria-label="Duración de la descarga"
            >
              {DOWNLOAD_DURATION_OPTIONS.map((min) => (
                <button
                  key={min}
                  type="button"
                  role="radio"
                  aria-checked={durationMinutes === min}
                  data-minutes={min}
                  className={`download-advanced-duration-option${
                    durationMinutes === min ? ' is-selected' : ''
                  }`}
                  onClick={() => setDurationMinutes(min)}
                >
                  {min} min
                </button>
              ))}
            </div>
            {!validRange && (
              <p className="download-advanced-error">
                {startValid && minDate && startDate.getTime() < minDate.getTime()
                  ? `Restricción de perfil: solo puedes descargar grabaciones desde ${formatDateTime(minDate)}.`
                  : startValid && maxDate && startDate.getTime() >= maxDate.getTime()
                    ? 'No puedes seleccionar una fecha futura: ese video aún no se ha grabado.'
                    : 'Selecciona una fecha y hora de inicio válida dentro de la grabación.'}
              </p>
            )}
            <p className="download-advanced-format-note">
              Se descargará un video de <strong>{durationMinutes} minutos</strong> desde la fecha y hora
              seleccionadas.
            </p>
          </div>

        </div>
        <div className="download-advanced-footer">
          <button type="button" className="download-advanced-btn secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="download-advanced-btn primary"
            onClick={handleConfirm}
            disabled={!validRange}
          >
            Descargar
          </button>
        </div>
      </div>
    </div>
  );
};
